import { OnlineStatusEnum } from 'src/app/shared/models/telemetry.model';

export enum TopicTypeEnum {
  TelemetryAlarm = 'TelemetryAlarm',
  ContractExpiration = 'ContractExpiration',
  LicenseExpiration = 'LicenseExpiration',
  Maintenance = 'Maintenance',
  MachineUptime = 'MachineUptime',
}
export enum AdminTopicTypeEnum {
  Cc3GenericNotificationTsc = 'Cc3GenericNotificationTsc',
}

export enum IncomingTopicCriticalityLevelEnum {
  CRITICAL = 'CRITICAL',
  MEDIUM = 'MEDIUM',
}

export enum IncomingTopicStatusEnum {
  NEW = 'NEW',
  CUSTOMER_CONTACTED = 'CUSTOMER_CONTACTED',
  OFFER_IN_PREPARATION = 'OFFER_IN_PREPARATION',
  OFFER_SENT = 'OFFER_SENT',
  RENEGOTIATION = 'RENEGOTIATION',
  OFFER_ACCEPTED = 'OFFER_ACCEPTED',
  WORK_ORDER_CREATED = 'WORK_ORDER_CREATED',
  REJECTED = 'REJECTED',
  CANCELLED = 'CANCELLED',
  COMPLETED = 'COMPLETED',
  PARTIALLY_COMPLETED = 'PARTIALLY_COMPLETED',
}

export const openIncomingTopicStatus: IncomingTopicStatusEnum[] = [
  IncomingTopicStatusEnum.CUSTOMER_CONTACTED,
  IncomingTopicStatusEnum.OFFER_IN_PREPARATION,
  IncomingTopicStatusEnum.OFFER_SENT,
  IncomingTopicStatusEnum.RENEGOTIATION,
  IncomingTopicStatusEnum.OFFER_ACCEPTED,
  IncomingTopicStatusEnum.WORK_ORDER_CREATED,
];

export interface EventOccurrenceDto {
  occurredAt: Date;
  occurredWith: string[] | null;
}

export interface TopicEventDto {
  subEventId: string;
  occurrences: EventOccurrenceDto[];
  occurrenceCount?: number;
}

export interface ChangeLogEntry {
  changedAt: Date;
  changedBy: string;
  status: string;
}

export interface IncomingTopicsDto {
  aggregateId: string;
  aggregateType: string;
  referencedAggregateType: string;
  referencedAggregateId: string;

  changeLog: ChangeLogEntry[];
  rejectedSubEvents: string[];

  createdAt: Date;
  createdBy: string;
  customerId: string | null;
  dueAt: Date | null;
  expiresAt: Date | null;
  machineId: string;
  workOrderId: string | null;

  criticalityLevel: IncomingTopicCriticalityLevelEnum;
  status: IncomingTopicStatusEnum;
  subEvents: TopicEventDto[];
  type: string;
  updatedAt: Date | null;
  updatedBy: string | null;
  workshopId: string;
  assignedResourceId: string | null;
}

export class IncomingTopicsSto {
  aggregateId: string;
  aggregateType: string;
  referencedAggregateType: string;
  referencedAggregateId: string;

  changeLog: ChangeLogEntry[];
  rejectedSubEvents: string[];

  createdAt: Date;
  createdBy: string;
  customerId: string | null;
  dueAt: Date | null;
  expiresAt: Date | null;
  machineId: string;
  workOrderId: string | null;

  criticalityLevel: IncomingTopicCriticalityLevelEnum;
  status: IncomingTopicStatusEnum;
  subEvents: TopicEventDto[];
  type: string;
  updatedAt: Date | null;
  updatedBy: string | null;
  workshopId: string;
  assignedResourceId: string | null;

  constructor(dto: IncomingTopicsDto) {
    this.aggregateId = dto?.aggregateId;
    this.aggregateType = dto?.aggregateType;
    this.referencedAggregateType = dto?.referencedAggregateType;
    this.referencedAggregateId = dto?.referencedAggregateId;

    this.changeLog = dto?.changeLog ?? [];
    this.rejectedSubEvents = dto?.rejectedSubEvents ?? [];

    this.createdAt = dto?.createdAt;
    this.createdBy = dto?.createdBy;
    this.customerId = dto?.customerId;
    this.dueAt = dto?.dueAt;
    this.expiresAt = dto?.expiresAt;
    this.machineId = dto?.machineId;
    this.workOrderId = dto?.workOrderId ?? null;
    this.criticalityLevel = dto?.criticalityLevel ?? IncomingTopicCriticalityLevelEnum.MEDIUM;
    this.status = dto?.status;

    this.subEvents = dto?.subEvents;
    this.type = dto?.type;
    this.updatedAt = dto?.updatedAt;
    this.updatedBy = dto?.updatedBy;
    this.workshopId = dto?.workshopId;
    this.assignedResourceId = dto?.assignedResourceId ?? null;
  }
}

export class IncomingTopicTableRow {
  // Columns
  rowId: string;
  colCriticalityLevel: IncomingTopicCriticalityLevelEnum;
  colStatus: IncomingTopicStatusEnum;
  colCreatedAt: Date;
  colDueAt: Date | null;
  colType: string;
  colTypeOrigin: IncomingTopicTypeOrigin | null;
  colAssignedResource: string | null;
  colDescription: string;
  colMaxiCare: Date | null;

  // administrative
  showCreateJobButton?: boolean | null;
  topicObject: IncomingTopicsSto;
  relatedWorkshop: RelatedWorkshopSto;
  relatedCustomer?: RelatedCustomerSto;
  relatedMachine: RelatedMachineSto;

  constructor(
    sto: IncomingTopicsSto,
    relatedCustomer: RelatedCustomerSto,
    relatedMachine: RelatedMachineSto,
    relatedWorkshop: RelatedWorkshopSto,
    description?: string,
    label?: string,
    assignedResourceName?: string | null,
    typeOrigin?: IncomingTopicTypeOrigin | null
  ) {
    this.rowId = sto?.aggregateId;
    this.colCriticalityLevel = sto?.criticalityLevel;
    this.colStatus = sto?.status;
    this.colCreatedAt = sto?.createdAt;
    this.colDueAt = sto?.dueAt;
    this.colType = label;
    this.colTypeOrigin = typeOrigin ?? null;
    this.colAssignedResource = assignedResourceName ?? null;
    this.colDescription = description;
    this.colMaxiCare = relatedMachine?.maxiCareEndDate ? new Date(relatedMachine.maxiCareEndDate) : null;
    this.topicObject = sto;
    this.relatedMachine = relatedMachine;
    this.relatedCustomer = relatedCustomer;
    this.relatedWorkshop = relatedWorkshop;
  }
}

export class RelatedObjectSto {
  aggregateType: string;
  aggregateId: string;
  constructor(aggregateType: string, aggregateId: string) {
    this.aggregateType = aggregateType;
    this.aggregateId = aggregateId;
  }
}

export interface RelatedMachineDto {
  aggregateType: string;
  aggregateId: string;
  serial: string;
  brandId: string;
  name: string;
  productFamily: string;
  maxiCareEndDate: Date | null;
  type: string;
  subtype: string;
}

export class RelatedMachineSto extends RelatedObjectSto {
  serial: string;
  brandId: string;
  name: string;
  productFamily: string;
  maxiCareEndDate: Date | null;
  type: string;
  subtype: string;

  constructor(dto: RelatedMachineDto) {
    super(dto?.aggregateType, dto?.aggregateId);
    this.aggregateId = dto?.aggregateId;
    this.aggregateType = dto?.aggregateType;
    this.serial = dto?.serial;
    this.brandId = dto?.brandId;
    this.name = dto?.name;
    this.productFamily = dto?.productFamily;
    this.maxiCareEndDate = dto?.maxiCareEndDate;
    this.type = dto?.type;
    this.subtype = dto?.subtype;
  }
}

export interface RelatedWorkshopDto {
  aggregateType: string;
  aggregateId: string;
  name: string;
  name2: string;
}

export class RelatedWorkshopSto extends RelatedObjectSto {
  name: string;
  name2: string;

  constructor(dto: RelatedWorkshopDto) {
    super(dto?.aggregateType, dto?.aggregateId);
    this.name = dto?.name;
    this.name2 = dto?.name2;
  }
}

export interface RelatedCustomerDto {
  aggregateType: string;
  aggregateId: string;
  customerId: string;
  name: string;
  name2: string;
}

export class RelatedCustomerSto extends RelatedObjectSto {
  customerId: string;
  name: string;
  name2: string;

  constructor(dto: RelatedCustomerDto) {
    super(dto?.aggregateType, dto?.aggregateId);
    this.customerId = dto?.customerId;
    this.name = dto?.name;
    this.name2 = dto?.name2;
  }
}

export interface IncomingTopicTypeDto {
  type: string;
  label: string;
  description: string;
  origin: IncomingTopicTypeOrigin;
  workshopId: string | null;
}

export interface RelatedIncomingTopicTypeDto {
  aggregateType: string;
  aggregateId: string;
  label: string;
  description: string | null;
  workshopId: string | null;
  origin: IncomingTopicTypeOrigin;
}

export class RelatedIncomingTopicTypeSto extends RelatedObjectSto {
  label: string;
  description: string | null;
  workshopId: string | null;
  origin: IncomingTopicTypeOrigin;

  constructor(dto: RelatedIncomingTopicTypeDto) {
    super(dto?.aggregateType, dto?.aggregateId);
    this.label = dto?.label;
    this.description = dto?.description ?? null;
    this.workshopId = dto?.workshopId ?? null;
    this.origin = dto?.origin;
  }
}

export class IncomingTopicRelatedTeaserItem {
  topic: IncomingTopicsSto;
  topicTypeLabel: string;
  customerName: string | null;
  machineName: string | null;
  machineSerial: string | null;

  constructor(
    topic: IncomingTopicsSto,
    topicTypeLabel: string,
    customerName: string | null = null,
    machineName: string | null = null,
    machineSerial: string | null = null
  ) {
    this.topic = topic;
    this.topicTypeLabel = topicTypeLabel;
    this.customerName = customerName;
    this.machineName = machineName;
    this.machineSerial = machineSerial;
  }
}

export interface ChangeStatusRequestDto {
  status: string;
  comment?: string;
}

export interface DtcTranslation {
  alarmText: string;
  dtc: string;
  infoText: string;
  machineType: string;
  severityLevel: number;
  troubleText: string | null;
  fmi: number | null;
  ccn: string | null;
}

// TODO check structure
export class DtcOccurrenceSelectedEvent {
  occurredAt: Date;
  operationHours: number;
  severityLevel: number;
  errorCodeType: string;
  errorCode: string;
  timestamp: Date;
  constructor(occ: EventOccurrenceDto) {
    const w = occ?.occurredWith;
    this.occurredAt = occ?.occurredAt;
    this.errorCodeType = w?.[0] != null ? w[0] : '';
    this.errorCode = w?.[1] != null ? w[1] : '';
    this.severityLevel = w?.[2] != null ? parseInt(w[2]) : NaN;
    this.timestamp = w?.[3] != null ? new Date(w[3]) : null;
    this.operationHours = w?.[4] != null ? parseFloat(w[4]) : NaN;
  }
}

export class PrEventOccurrenceSelectedEvent {
  occurredAt: Date;
  operationHoursFrom: number;
  operationHoursUntil: number;
  timestampFrom: Date;
  timestampUntil: Date;
  severityLevel: number;

  constructor(occ: EventOccurrenceDto) {
    const w = occ?.occurredWith;
    this.occurredAt = occ?.occurredAt;
    this.timestampFrom = w?.[4] != null ? new Date(w[4]) : null;
    this.timestampUntil = w?.[5] != null ? new Date(w[5]) : null;
    this.operationHoursFrom = w?.[6] != null ? parseFloat(w[6]) : NaN;
    this.operationHoursUntil = w?.[7] != null ? parseFloat(w[7]) : NaN;
    this.severityLevel = w?.[3] != null ? parseInt(w[3]) : NaN;
  }
}

export interface MachineLatestState {
  machineId: string;
  onlineStatus: OnlineStatusEnum;
  mostRelevantAlarm: unknown;
  latestContact: Date;
  signals: {
    signalId: string;
    value: unknown;
    unit: string;
    measuredAt: Date;
    uploadedAt: Date;
  }[];
}

export interface IncomingTopicTileDto {
  type: string;
  allMedium: number;
  allCritical: number;
  newMedium: number;
  newCritical: number;
  inProgressMedium: number;
  inProgressCritical: number;
}

export type IncomingTopicTypeOrigin = 'SYSTEM' | 'ACTIVITY_CLAAS' | 'ACTIVITY_DEALER';
